import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Box, Button, Grid, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import EstadoErrorApp from '../components/comun/EstadoErrorApp';
import {
  AvatarApp,
  ContainerApp,
  ListItemTextApp,
  LoadingApp,
  PaperApp,
  TextFieldEditApp,
  TypographyApp,
  VisibilidadIconApp,
} from '../components/custom/CustomComponents';
import FiltrosLista from '../components/listas/FiltrosLista';
import JugadoresLista from '../components/listas/JugadoresLista';
import ListasUsuario from '../components/listas/ListasUsuario';
import ModalAnadirJugadorLista from '../components/listas/ModalAnadirJugadorLista';
import { Context } from '../service/Context';
import { generarTemporadasAdyacentes, getInicialesNombre, ordenarLista } from '../utils/UtilesComun';
import { obtenerCategoriaEstimada } from '../utils/UtilesJugador';

/**
 * Pagina de una lista de jugadores.
 */
const Lista = () => {
  const {
    obtenerLista,
    idUsuario,
    modificarVisibilidadLista,
    modificarTemporadaLista,
    obtenerListasAccesibles,
    listasAccesibles,
    temporadaActual,
    renombrarLista,
    eliminarLista,
    insertarJugadorLista,
    eliminarJugadorLista,
  } = useContext(Context);
  const { routeIdLista } = useParams();
  const navigate = useNavigate();

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const [nombreLista, setNombreLista] = useState(undefined);
  const [temporadaLista, setTemporadaLista] = useState('');
  const [lista, setLista] = useState(undefined);
  const [visibilidad, setVisibilidad] = useState('PRIVADA');
  const [esPropietario, setEsPropietario] = useState(false);
  const [creadorLista, setCreadorLista] = useState(null);
  const [allTemporadas, setAllTemporadas] = useState([]);
  const [anosFiltro, setAnosFiltro] = useState([1999, 2022]);
  const [demarcacionesFiltro, setDemarcacionesFiltro] = useState([]);
  const [lateralidadesFiltro, setLateralidadesFiltro] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState({ field: 'nombre', direction: 'asc' });

  // Estados de carga independientes: sidebar vs detalle de la lista seleccionada
  const [, setLoadingSidebar] = useState(true);
  const [loadingDetalle, setLoadingDetalle] = useState(Boolean(routeIdLista));

  // Listas accesibles para el sidebar (inicializado con listasAccesibles del contexto si ya existen)
  const [todasListas, setTodasListas] = useState(() => listasAccesibles || []);
  const [openAddMenu, setOpenAddMenu] = useState(false);

  // Sincronizar listas accesibles si se actualizan en el contexto
  useEffect(() => {
    if (listasAccesibles && listasAccesibles.length > 0) {
      setTodasListas(listasAccesibles);
      setLoadingSidebar(false);
    }
  }, [listasAccesibles]);

  // Recarga bajo demanda del sidebar tras crear, eliminar, renombrar o mutar una lista
  const recargarSidebar = useCallback(async () => {
    if (idUsuario) {
      const listasAcc = await obtenerListasAccesibles(true);
      setTodasListas(listasAcc || []);
    }
  }, [idUsuario, obtenerListasAccesibles]);

  // Carga inicial del listado de listas para el sidebar sólo si no estaban precargadas
  useEffect(() => {
    let isMounted = true;
    const initSidebar = async () => {
      if (idUsuario && (!listasAccesibles || listasAccesibles.length === 0)) {
        const listasAcc = await obtenerListasAccesibles(false);
        if (isMounted && listasAcc) {
          setTodasListas(listasAcc);
          setLoadingSidebar(false);
        }
      } else if (listasAccesibles && listasAccesibles.length > 0) {
        setLoadingSidebar(false);
      }
    };
    initSidebar();
    return () => {
      isMounted = false;
    };
  }, [idUsuario, listasAccesibles, obtenerListasAccesibles]);

  // Control de la lista actualmente en pantalla o cargándose para evitar parpadeos y recargas redundantes
  const idCargadoRef = useRef(null);

  // Carga de la lista seleccionada (sólo la columna derecha)
  const cargarDetalleLista = useCallback(
    async (idLista, forzar = false) => {
      if (!idLista) {
        idCargadoRef.current = null;
        setLista(undefined);
        setNombreLista(undefined);
        setLoadingDetalle(false);
        return;
      }

      // Si ya está cargada o en proceso de carga y no es una recarga forzada, no repetir
      if (!forzar && idCargadoRef.current === idLista) {
        return;
      }

      idCargadoRef.current = idLista;
      setLoadingDetalle(true);
      if (forzar) {
        setLista(undefined);
        setNombreLista(undefined);
      }

      try {
        const datosLista = await obtenerLista(idLista);
        if (idCargadoRef.current !== idLista) {
          return;
        }

        if (datosLista) {
          setNombreLista(datosLista.nombre);
          setLista(datosLista.jugadores || []);
          setVisibilidad(datosLista.visibilidad || 'PRIVADA');
          setTemporadaLista(datosLista.temporada || '');
          setEsPropietario(datosLista.idUsuario === idUsuario);
          setCreadorLista(datosLista.nombreUsuario || 'Desconocido');

          const tempsObj = new Set(generarTemporadasAdyacentes(temporadaActual));
          if (datosLista.temporada) {
            tempsObj.add(datosLista.temporada);
          }
          setAllTemporadas(Array.from(tempsObj).sort((a, b) => b.localeCompare(a)));
        } else {
          setLista(null);
        }
      } catch (err) {
        console.error('Error al cargar lista:', err);
        if (idCargadoRef.current === idLista) {
          setLista(null);
        }
      } finally {
        if (idCargadoRef.current === idLista) {
          setLoadingDetalle(false);
        }
      }
    },
    [idUsuario, obtenerLista, temporadaActual]
  );

  useEffect(() => {
    cargarDetalleLista(routeIdLista);
  }, [routeIdLista, cargarDetalleLista]);

  // Resetear paginación al cambiar de lista
  useEffect(() => {
    setPage(0);
  }, [routeIdLista]);

  // Refresco suave de datos (ej. añadir/eliminar jugadores) sin desmontar tabla ni parpadeos
  const refrescarDetalleSinFlicker = useCallback(async () => {
    if (!routeIdLista) {
      return;
    }
    const datosLista = await obtenerLista(routeIdLista);
    if (datosLista && idCargadoRef.current === routeIdLista) {
      setNombreLista(datosLista.nombre);
      setLista(datosLista.jugadores || []);
      setVisibilidad(datosLista.visibilidad || 'PRIVADA');
      setTemporadaLista(datosLista.temporada || '');
      setEsPropietario(datosLista.idUsuario === idUsuario);
      setCreadorLista(datosLista.nombreUsuario || 'Desconocido');
    }
  }, [routeIdLista, idUsuario, obtenerLista]);

  const handleVisibilidadChange = async (e) => {
    const newVal = e.target.value;
    setVisibilidad(newVal);
    await modificarVisibilidadLista(routeIdLista, newVal);
    recargarSidebar();
  };

  const handleTemporadaListaChange = async (e) => {
    const newVal = e.target.value;
    setTemporadaLista(newVal);
    await modificarTemporadaLista(routeIdLista, newVal);
    recargarSidebar();
  };

  const handleEditClick = () => {
    setRenameValue(nombreLista);
    setIsRenaming(true);
  };

  const handleConfirmRename = async () => {
    if (renameValue.trim() && renameValue.trim() !== nombreLista) {
      await renombrarLista(routeIdLista, renameValue.trim());
      setNombreLista(renameValue.trim());
      recargarSidebar();
    }
    setIsRenaming(false);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
  };

  const handleDeleteLista = async () => {
    const res = await eliminarLista(routeIdLista);
    if (res) {
      await recargarSidebar();
      navigate('/');
    }
  };

  const handleAddJugador = async (jugador) => {
    if (jugador && jugador.idJugador) {
      await insertarJugadorLista(routeIdLista, jugador.idJugador);
      setOpenAddMenu(false);
      await refrescarDetalleSinFlicker();
    }
  };

  const handleRemoveJugador = async (idJugador) => {
    await eliminarJugadorLista(routeIdLista, idJugador);
    await refrescarDetalleSinFlicker();
  };

  // Aplicar filtros y enriquecer datos (categoría)
  const listaFiltrada = useMemo(() => {
    if (!Array.isArray(lista)) {
      return [];
    }
    const tempAct = temporadaLista || temporadaActual;
    return lista
      .filter(
        (jugador) =>
          (jugador.ano === null || (jugador.ano >= anosFiltro[0] && jugador.ano <= anosFiltro[1])) &&
          (demarcacionesFiltro.length === 0 || demarcacionesFiltro.includes(jugador.demarcacion)) &&
          (lateralidadesFiltro.length === 0 || lateralidadesFiltro.includes(jugador.lateralidad))
      )
      .map((jugador) => {
        const cat = jugador.ano ? obtenerCategoriaEstimada(jugador.ano, tempAct) : null;
        return {
          ...jugador,
          categoria: cat?.codigo || '',
        };
      });
  }, [lista, anosFiltro, demarcacionesFiltro, lateralidadesFiltro, temporadaLista, temporadaActual]);

  const jugadoresOrdenados = useMemo(() => {
    return ordenarLista(listaFiltrada, sort);
  }, [listaFiltrada, sort]);

  const jugadoresPaginados = jugadoresOrdenados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleObservacionUpdate = (idJugador, updatedObservaciones) => {
    setLista((prevLista) =>
      prevLista.map((jugador) =>
        jugador.idJugador === idJugador ? { ...jugador, observaciones: updatedObservaciones } : jugador
      )
    );
  };

  const listaSeleccionada = routeIdLista && Array.isArray(lista);
  const canEdit = esPropietario || visibilidad === 'COMPARTIDA';

  return (
    <>
      <ContainerApp maxWidth={false}>
        <Grid container spacing={3}>
          {/* Barra lateral: Listado de listas (permanece siempre montado para no perder scroll ni foco) */}
          <Grid item xs={12} md={3} sx={{ borderRight: { md: '1px solid rgba(0, 0, 0, 0.08)' }, pr: { md: 4 } }}>
            <PaperApp sx={{ height: 'fit-content' }}>
              <ListasUsuario
                todasListas={todasListas}
                initialTemporada={temporadaLista}
                selectedIdLista={routeIdLista}
                onUpdate={recargarSidebar}
              />
            </PaperApp>
          </Grid>

          {/* Columna derecha: Detalle de la lista seleccionada o estado de carga/vacío */}
          <Grid item xs={12} md={9}>
            {loadingDetalle ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 450 }}>
                <LoadingApp />
              </Box>
            ) : listaSeleccionada ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                    width: '100%',
                  }}
                >
                  {/* Modal o bloque para renombrar lista */}
                  {isRenaming ? (
                    <Box sx={{ flexGrow: 1, mr: 2, minWidth: 220 }}>
                      <TextFieldEditApp
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onConfirm={handleConfirmRename}
                        onCancel={handleCancelRename}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Nombre de la lista */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1.25rem', sm: '1.45rem' },
                          color: 'text.primary',
                          lineHeight: 1.2,
                        }}
                      >
                        {nombreLista}
                      </Typography>
                      {esPropietario && (
                        <Tooltip title="Renombrar lista" arrow>
                          <IconButton
                            onClick={handleEditClick}
                            size="small"
                            sx={{
                              color: '#64748b',
                              p: 0.5,
                              borderRadius: '6px',
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: '#f1f5f9',
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}

                  {/* Divisor vertical suave */}
                  <Box sx={{ borderLeft: '1px solid #e2e8f0', height: 24, mx: 0.5 }} />

                  {/* Metadato 1: Autor de la lista */}
                  <Tooltip title={`Creador: ${creadorLista || 'Desconocido'}`} arrow>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1,
                        py: 0.5,
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        height: 32,
                      }}
                    >
                      <AvatarApp size={22} esPropio={esPropietario} sx={{ fontSize: '0.65rem' }}>
                        {creadorLista ? getInicialesNombre(creadorLista) : '?'}
                      </AvatarApp>
                      <TypographyApp
                        sx={{
                          fontSize: '0.80rem',
                          fontWeight: 600,
                          color: '#334155',
                          maxWidth: 130,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {creadorLista || 'Autor'}
                      </TypographyApp>
                    </Box>
                  </Tooltip>

                  {/* Metadato 2: Temporada de la lista */}
                  {esPropietario ? (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 32,
                        px: 0.75,
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Select
                        value={temporadaLista || ''}
                        onChange={handleTemporadaListaChange}
                        variant="standard"
                        disableUnderline
                        sx={{
                          fontSize: '0.80rem',
                          fontWeight: 600,
                          color: '#1e293b',
                          '& .MuiSelect-select': { py: 0, pl: 0.5, pr: '18px !important' },
                          '& .MuiSelect-icon': { color: '#64748b', fontSize: '1rem', right: 0 },
                        }}
                      >
                        {allTemporadas.map((temp) => (
                          <MenuItem key={temp} value={temp} sx={{ fontSize: '0.80rem' }}>
                            {temp}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  ) : (
                    temporadaLista && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.25,
                          height: 32,
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                        }}
                      >
                        <TypographyApp sx={{ color: '#475569', fontSize: '0.80rem', fontWeight: 600 }}>
                          {temporadaLista}
                        </TypographyApp>
                      </Box>
                    )
                  )}

                  {/* Metadato 3: Visibilidad de la lista */}
                  {esPropietario ? (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        height: 32,
                        px: 1,
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <VisibilidadIconApp visibilidad={visibilidad} sx={{ fontSize: '0.95rem', color: '#0f766e' }} />
                      <Select
                        value={visibilidad}
                        onChange={handleVisibilidadChange}
                        variant="standard"
                        disableUnderline
                        renderValue={(selected) => {
                          if (selected === 'PRIVADA') {
                            return 'Privada';
                          }
                          if (selected === 'COMPARTIDA') {
                            return 'Compartida';
                          }
                          if (selected === 'PUBLICA') {
                            return 'Pública';
                          }
                          return selected;
                        }}
                        sx={{
                          fontSize: '0.80rem',
                          fontWeight: 600,
                          color: '#1e293b',
                          '& .MuiSelect-select': { py: 0, pl: 0.25, pr: '18px !important' },
                          '& .MuiSelect-icon': { color: '#64748b', fontSize: '1rem', right: 0 },
                        }}
                      >
                        <MenuItem value="PRIVADA" sx={{ fontSize: '0.80rem' }}>
                          <ListItemTextApp primary="Privada" secondary="Solo tú puedes verla y editarla" />
                        </MenuItem>
                        <MenuItem value="PUBLICA" sx={{ fontSize: '0.80rem' }}>
                          <ListItemTextApp primary="Pública" secondary="Todos la ven, solo tú puedes editarla" />
                        </MenuItem>
                        <MenuItem value="COMPARTIDA" sx={{ fontSize: '0.80rem' }}>
                          <ListItemTextApp primary="Compartida" secondary="Todos la ven y pueden colaborar" />
                        </MenuItem>
                      </Select>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        height: 32,
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                      }}
                    >
                      <VisibilidadIconApp visibilidad={visibilidad} sx={{ fontSize: '0.95rem', color: '#0f766e' }} />
                      <TypographyApp sx={{ color: '#475569', fontSize: '0.80rem', fontWeight: 600 }}>
                        {visibilidad === 'PUBLICA'
                          ? 'Pública'
                          : visibilidad === 'COMPARTIDA'
                            ? 'Compartida'
                            : 'Privada'}
                      </TypographyApp>
                    </Box>
                  )}

                  {/* Espacio elástico para empujar los botones de acción a la derecha */}
                  <Box sx={{ flexGrow: 1 }} />

                  {/* Botón Añadir Jugador */}
                  {canEdit && (
                    <Button
                      variant="contained"
                      onClick={() => setOpenAddMenu(true)}
                      startIcon={<PersonAddIcon sx={{ fontSize: '1.05rem' }} />}
                      sx={{
                        height: 34,
                        px: 1.75,
                        borderRadius: '6px',
                        fontSize: '0.80rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: '#0f766e',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          backgroundColor: '#0d665f',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      Añadir jugador
                    </Button>
                  )}

                  {/* Botón Eliminar Lista */}
                  {esPropietario && (
                    <Button
                      variant="outlined"
                      onClick={handleDeleteLista}
                      startIcon={<DeleteIcon sx={{ fontSize: '1.05rem' }} />}
                      sx={{
                        height: 34,
                        px: 1.5,
                        borderRadius: '6px',
                        fontSize: '0.80rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        color: '#dc2626',
                        borderColor: '#fecaca',
                        backgroundColor: '#fef2f2',
                        '&:hover': {
                          borderColor: '#f87171',
                          backgroundColor: '#fee2e2',
                        },
                      }}
                    >
                      Eliminar lista
                    </Button>
                  )}
                </Box>

                {/* Divider sutil */}
                <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 2, width: '100%' }} />

                {/* Filtros de la lista */}
                <FiltrosLista
                  anosFiltro={anosFiltro}
                  setAnosFiltro={setAnosFiltro}
                  demarcacionesFiltro={demarcacionesFiltro}
                  setDemarcacionesFiltro={setDemarcacionesFiltro}
                  lateralidadesFiltro={lateralidadesFiltro}
                  setLateralidadesFiltro={setLateralidadesFiltro}
                  minAno={1999}
                  maxAno={2022}
                />

                {/* Tabla de jugadores */}
                <JugadoresLista
                  lista={jugadoresPaginados}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  totalCount={jugadoresOrdenados.length}
                  sort={sort}
                  setSort={setSort}
                  onUpdate={handleObservacionUpdate}
                  canEdit={canEdit}
                  onDelete={handleRemoveJugador}
                  temporada={temporadaLista || temporadaActual}
                />
              </>
            ) : routeIdLista && lista === null ? (
              <EstadoErrorApp
                titulo="No se pudo cargar la lista seleccionada"
                subtitulo="No te preocupes, esto suele ser temporal. Prueba esto:"
                onReintentar={() => cargarDetalleLista(routeIdLista, true)}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 450 }}>
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <TypographyApp sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Selecciona una lista del panel lateral
                  </TypographyApp>
                  <TypographyApp sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                    o crea una nueva lista
                  </TypographyApp>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </ContainerApp>

      {/* Modal moderno para añadir jugador a la lista */}
      <ModalAnadirJugadorLista
        open={openAddMenu}
        onClose={() => setOpenAddMenu(false)}
        onAddJugador={handleAddJugador}
        nombreLista={nombreLista}
      />
    </>
  );
};

Lista.propTypes = {
  nombreLista: PropTypes.string,
};

export default Lista;
